import { safeLocalKv, startRunOptions } from '../flow';
import type { DailyChoice } from './dailySeed';
import { loadContent } from '../../data/content';
import { hubState, type HubView } from './hubState';
import { useEffect, useRef } from 'preact/hooks';
import { CLASS_NAME, loadChosenClass, saveChosenClass } from '../classes';
import { ClassSelect } from './ClassSelect';
import { AudioSettingsPanel } from '../audio/AudioControls';
import { Talents } from './panels/Talents';
import { Achievements } from './panels/Achievements';
import { Unlocks } from './panels/Unlocks';
import { Leaderboard } from './panels/Leaderboard';
import { DailyButton } from './DailyButton';
import type { Meta } from '../../net/progress';
import type { RunSummary } from '../../core/run';
import type { LeaderboardApi } from '../../net/leaderboard';
import type Phaser from 'phaser';

export { hubState };
const content = loadContent();

/** Plein écran + verrouillage en paysage quand c'est possible ; un refus du navigateur ne bloque jamais le jeu. */
export async function enterFullscreen(): Promise<void> {
  try { await document.documentElement.requestFullscreen?.({ navigationUI: 'hide' }); } catch { /* refusé : on joue quand même */ }
  try { await (screen.orientation as ScreenOrientation & { lock?: (o: string) => Promise<void> }).lock?.('landscape'); } catch { /* non supporté */ }
}

/**
 * Modification de méta depuis le hub (talents, débloquables, modificateurs) : `apply` est calculée par le store sur SA méta
 * (seule source de vérité), puis le hub affiche la méta qu'il renvoie. Sans store (hub isolé), elle s'applique à la méta affichée.
 */
function changeMeta(apply: (m: Meta) => Meta | null): void {
  const store = hubState.store.value;
  if (!store) { const next = apply(hubState.meta.value); if (next) hubState.meta.value = next; return; }
  store.update(apply).then((m) => { hubState.meta.value = m; }, (e: unknown) => { console.warn('sauvegarde de la méta impossible', e); });
}

/** Boutons de l'accueil qui ouvrent une vue (la clé sert aussi à rendre le focus au bouton d'origine). */
const NAV: { view: Exclude<HubView, 'home'>; label: string }[] = [
  { view: 'talents', label: 'Talents' }, { view: 'succes', label: 'Succès' }, { view: 'debloquables', label: 'Débloquables' }, { view: 'classements', label: 'Classements' },
];

interface HomeProps { meta: Meta; last: RunSummary | null; fresh: string[]; pseudo: string; api: LeaderboardApi | null; onDaily: (daily: DailyChoice) => void }

/** Accueil du hub ; l'état (signaux) est lu par `Hub` et passé en props pour que le rendu suive le parent. */
function Home({ meta: m, last, fresh, pseudo, api, onDaily }: HomeProps) {
  const panel = useRef<HTMLDivElement>(null);
  // focus : bouton qui a ouvert la vue qu'on vient de quitter, sinon « Choisir sa classe »
  useEffect(() => {
    const opener = hubState.opener.value;
    hubState.opener.value = null;
    const target = (opener && panel.current?.querySelector<HTMLElement>(`[data-view="${opener}"]`)) || panel.current?.querySelector<HTMLElement>('[data-view="classes"]');
    target?.focus({ preventScroll: true });
  }, []);
  const open = (view: HubView) => { hubState.opener.value = view; hubState.view.value = view; };
  return (
    <div class="panel hub-panel" ref={panel}>
      <div class="hub-scroll">
        <img class="hub-logo" src="/assets/ui/logo-600.png" alt="RogFus Roguelike" />
        <p class="hub-pseudo">{pseudo}</p>
        <p class="hub-class">Classe : {CLASS_NAME[loadChosenClass(safeLocalKv())] ?? 'Xélor'}</p>
        <p>Reflets Oniriques : <b>{m.reflets}</b> · Ogrines : <b>{m.ogrines}</b></p>
        {last && <p class="last-run">Dernière run : {last.outcome === 'victory' ? 'victoire' : 'mort'}, {last.roomsCleared} salles nettoyées, {last.kills} kills, +{last.refletsEarned} Reflets</p>}
        {fresh.length > 0 && <p class="new-achievements">Nouveaux succès : {fresh.map((id) => content.achievements[id]?.name ?? id).join(', ')}</p>}
        <div class="hub-actions">
          <button type="button" data-view="classes" onClick={() => open('classes')}>Choisir sa classe</button>
          <DailyButton api={api} className="hub-daily" onDaily={onDaily} />
        </div>
        <div class="hub-meta-actions">
          {NAV.map((n) => <button key={n.view} type="button" data-view={n.view} onClick={() => open(n.view)}>{n.label}</button>)}
        </div>
        {document.fullscreenEnabled && <div class="hub-extra-actions"><button type="button" class="fullscreen-btn" onClick={() => { void enterFullscreen(); }}>Plein écran</button></div>}
        <AudioSettingsPanel />
        <p class="credits">DOFUS, son univers, ses personnages et ses graphismes sont la propriété d'Ankama. Projet de fan indépendant, non officiel et non commercial.</p>
      </div>
    </div>
  );
}

export function Hub({ game }: { game: Phaser.Game }) {
  if (!hubState.visible.value) return null;
  const kv = safeLocalKv();
  const play = (classId: string, daily: DailyChoice | null = null) => {
    const chosen = saveChosenClass(kv, classId);
    hubState.visible.value = false; hubState.view.value = 'home'; hubState.opener.value = null;
    hubState.newAchievements.value = []; // annoncés une seule fois : la run qui démarre les a vus
    game.scene.stop('Hub'); game.scene.start('Room', startRunOptions(hubState.meta.value, content, chosen, daily));
  };
  const home = () => { hubState.view.value = 'home'; };
  const playDaily = (daily: DailyChoice) => play(loadChosenClass(kv), daily);
  switch (hubState.view.value) {
    case 'classes': return <ClassSelect selected={loadChosenClass(kv)} onPlay={(id) => play(id)} onBack={home} />;
    case 'talents': return <Talents meta={hubState.meta.value} content={content} onChange={changeMeta} onBack={home} />;
    case 'succes': return <Achievements meta={hubState.meta.value} content={content} onBack={home} />;
    case 'debloquables': return <Unlocks meta={hubState.meta.value} content={content} onChange={changeMeta} onBack={home} />;
    case 'classements': return <Leaderboard api={hubState.leaderboard.value} pseudo={hubState.pseudo.value} onPseudo={(p) => { hubState.pseudo.value = p; }} onDaily={playDaily} onBack={home} />;
    default: return <Home meta={hubState.meta.value} last={hubState.lastRun.value} fresh={hubState.newAchievements.value} pseudo={hubState.pseudo.value} api={hubState.leaderboard.value} onDaily={playDaily} />;
  }
}
